/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Icon } from '@/components/icon';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FILTERS_OPEN } from '@/hooks/use-filters-panel-state';
import type { PanelState } from '@/components/category-refinements/panel/use-panel-presentation';

export interface FiltersButtonProps {
    /** Callback when button is clicked */
    onClick: () => void;
    /** Whether the filters panel is currently shown */
    isActive?: boolean;
    /**
     * The panel's declared state from the URL, when the caller has one.
     *
     * Supplying it moves the FILL off `isActive` and onto CSS. `isActive` is resolved from a
     * breakpoint (`useFiltersVisible`), which no server can read: with the parameter absent it
     * evaluates to `true` on the server and on the first client render at every width, so below
     * `lg` — where the panel is an overlay and is closed — the button painted filled and then
     * flipped to outline once `matchMedia` reported. `panelState` is deterministic, so `open`
     * fills at every width, `closed` never fills, and `default` fills at `lg`+ through the media
     * query below, which is exactly when the sidebar shows. `isActive` is then left to drive
     * `aria-pressed` alone — an attribute correction after hydration costs no repaint.
     *
     * Omit it and the fill falls back to `isActive`, which is what every caller with a
     * non-breakpoint-derived state wants.
     */
    panelState?: PanelState;
    /** Number of selected filters currently applied */
    selectedFiltersCount?: number;
    /** Additional CSS classes */
    className?: string;
}

/**
 * Button component that toggles the filters panel.
 */
export default function FiltersButton({
    onClick,
    isActive = false,
    panelState,
    selectedFiltersCount = 0,
    className,
}: FiltersButtonProps) {
    const { t } = useTranslation();
    const filtersLabel = t('categoryRefinements:filtersButtonLabel');
    const normalizedSelectedFiltersCount = Math.max(0, Math.floor(selectedFiltersCount));
    const hasSelectedFilters = normalizedSelectedFiltersCount > 0;
    const filtersAriaLabel = hasSelectedFilters
        ? t('categoryRefinements:filtersButtonLabelWithCount', {
              label: filtersLabel,
              count: normalizedSelectedFiltersCount,
          })
        : filtersLabel;

    // The fill, resolved without reading a breakpoint. See `panelState` on the props.
    const isFilled = panelState ? panelState === FILTERS_OPEN : isActive;

    return (
        <Button
            variant={isFilled ? 'default' : 'outline'}
            data-filters={panelState}
            onClick={onClick}
            className={cn(
                'text-sm font-normal leading-5 tracking-[-0.15px]',
                isFilled ? 'text-primary-foreground' : 'text-foreground',
                // The `default` state, filled at `lg`+ only. This mirrors the sidebar's own
                // `lg:block lg:data-[filters=closed]:hidden` default rather than re-deriving it,
                // so the control and the panel it reveals agree on the SERVER, not one render
                // later. The three declarations are exactly what `variant="default"` supplies
                // over `variant="outline"`; there is no shadow to neutralise because
                // `--ui-shadow` is already `0 0 #0000` (core.css), and no radius because
                // `--ui-radius` is `0`.
                //
                // Written out literally, twice over, because Tailwind scans source text — a
                // composed `${prefix}${class}` would generate nothing.
                'lg:data-[filters=default]:border-transparent lg:data-[filters=default]:bg-primary lg:data-[filters=default]:text-primary-foreground',
                'lg:data-[filters=default]:hover:bg-primary-hover lg:data-[filters=default]:active:bg-primary-hover',
                className
            )}
            aria-label={filtersAriaLabel}
            aria-pressed={isActive}>
            {/* `page_info` from the design (CRCB2B-87), replacing lucide's `Funnel`. The name
                reads oddly for a filter control, but the glyph is right: two rows, each a bar
                with a round knob, knob left then knob right. Confirmed against Figma
                `11546:79050`; `filter_list` (three plain bars) and `tune` (three rows) are the
                two near-misses, so do not "simplify" it back to either.

                Unlike the other glyphs used here it was NOT in the approved subset, so
                `glyphs.ts` gained an entry and `public/fonts/material-symbols-sharp-subset.woff2`
                was regenerated with `pnpm icons:subset` — both must ship together or the button
                renders the literal text `page_info`.

                No `size` and no margin: Button sizes its own icon slot via
                `[&_[data-slot=icon]]:text-[24px]`, and spaces it with its own `gap-2`. The old
                `size-4 mr-2` fought both — passing `size` writes an inline font-size that
                outranks the Button's size axis, and `mr-2` doubled the gap. */}
            <Icon name="page_info" />
            {filtersLabel}
            {hasSelectedFilters && (
                <Badge
                    variant="neutral"
                    appearance="clear"
                    // `bg-background` is a deliberate override, not an appearance the
                    // design defines: this count chip sits ON the filled Filters
                    // button, so it needs an opaque white fill rather than the
                    // `clear` appearance's transparency. Figma has no white badge
                    // cell (CRCB2B-640). The rest is layout.
                    className="ml-2 min-w-5 bg-background px-1.5"
                    aria-hidden>
                    {normalizedSelectedFiltersCount}
                </Badge>
            )}
        </Button>
    );
}
